import React from 'react';
import PropTypes from 'prop-types';
import style from './JobCard.module.sass';

const JobCard = ({
  job, isDragging, onOpenDetail, showPhotographers,
}) => {
  const photographers = job.photographers || [];

  // Meta line: "<trigs> trigs · <job> · <start>–<end>". starttime is free-form
  // (often already a range); end_time, when set, is appended.
  const timeLabel = [job.start_time, job.end_time].filter(Boolean).join(' – ');
  const metaParts = [`${job.trigs || 0} trigs`, job.job || '-'];
  if (timeLabel) metaParts.push(timeLabel);

  // One-week-only view: after the times, show booked senior appointments and the
  // total hours booked. Only for jobs that actually have appointments (senior
  // sittings); hidden as noise otherwise.
  const formatBooked = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h && m) return `${h}h ${m}m`;
    return h ? `${h}h` : `${m}m`;
  };
  if (showPhotographers && job.appt_count > 0) {
    metaParts.push(`${job.appt_count} appts`);
    if (job.booked_minutes > 0) metaParts.push(`${formatBooked(job.booked_minutes)} booked`);
  }

  // "Count per photographer" = last year's distinct students ÷ this year's rigs.
  // Only shown when there's real prior-year data (a 0/no-match is hidden as noise).
  const lastYear = job.photographed_last_year;
  const showLastYear = lastYear > 0;
  const perPhotographer = (showLastYear && photographers.length > 0)
    ? Math.round(lastYear / photographers.length)
    : null;
  const lastYearTitle = `${job.location ? `${job.location} — ` : ''}`
    + 'distinct students photographed last year (total, and per rig this year)';

  const handleMenuClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onOpenDetail) {
      onOpenDetail(job);
    }
  };

  return (
    <div className={`${style.jobCard} ${isDragging ? style.dragging : ''}`}>
      <div className={style.cardHeader}>
        <div className={`${style.schoolName} ${job.has_changes ? style.changed : ''}`}>
          {job.school_name}
        </div>
        <button
          type="button"
          className={style.menuButton}
          onClick={handleMenuClick}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          title="View details"
        >
          &#8942;
        </button>
      </div>
      <div className={style.trigs}>{metaParts.join(' · ')}</div>
      {showPhotographers && (
        <div className={style.photographerSection}>
          <div className={style.photographers}>
            {photographers.length > 0 ? (
              photographers.map((p) => (
                <div
                  key={p.id}
                  className={style.photographerRow}
                  title={[p.rig, p.name].filter(Boolean).join(' ')}
                >
                  <span className={style.rigName}>{p.rig}</span>
                  {p.name}
                </div>
              ))
            ) : (
              <span className={style.noPhotographer}>No photographer assigned</span>
            )}
          </div>
          {showLastYear && (
            <div className={style.lastYear} title={lastYearTitle}>
              <span className={style.lastYearLabel}>{job.location || '’25'}</span>
              <span className={style.lastYearTotal}>{lastYear}</span>
              {perPhotographer != null && (
                <span className={style.lastYearPer}>
                  {perPhotographer}
                  /ea
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

JobCard.propTypes = {
  job: PropTypes.shape({
    school_name: PropTypes.string,
    has_changes: PropTypes.bool,
    photographers: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      rig: PropTypes.string,
      name: PropTypes.string,
    })),
    trigs: PropTypes.number,
    job: PropTypes.string,
    start_time: PropTypes.string,
    end_time: PropTypes.string,
    location: PropTypes.string,
    photographed_last_year: PropTypes.number,
    appt_count: PropTypes.number,
    booked_minutes: PropTypes.number,
  }).isRequired,
  isDragging: PropTypes.bool.isRequired,
  onOpenDetail: PropTypes.func.isRequired,
  showPhotographers: PropTypes.bool.isRequired,
};

export default JobCard;
